// searchsync-toggle-refinement-component.js

import { configure } from 'instantsearch.js/es/widgets';
import { getInstance, registerWidget } from './searchsync-create-instance';

/**
 * Toggle Refinement: a single on/off checkbox that filters an attribute to one value.
 *
 * Implemented like the Favorites widget (a dedicated `configure` widget carrying a facetFilter),
 * NOT via InstantSearch's `toggleRefinement` / `connectToggleRefinement`. The connector manages the
 * attribute as a disjunctive facet and calls `removeDisjunctiveFacetRefinement()` on the whole
 * attribute every render, which (a) crashes when the attribute is ALSO a menu (hierarchical) facet
 * and (b) stomps a refinement-list on the same attribute — so a shared attribute left the checkbox
 * unable to stay checked (each render synced it back off). A facetFilter sidesteps all of that: the
 * native checkbox is the source of truth (so it always toggles visually) and the filter is an
 * independent AND clause that coexists with any other widget on the same attribute.
 */
function applyToggleFilter( { search, configureRef, checked, attribute, on } ) {
  // Remove any previously-added Configure widget first so unchecking cleanly restores results.
  if ( configureRef.current ) {
    try {
      search.removeWidgets( [ configureRef.current ] );
    } catch ( _ ) {
      // no-op: widget may already be detached.
    }
    configureRef.current = null;
  }

  if ( checked && attribute && on !== undefined && on !== '' ) {
    // Array-of-arrays = AND of ORs (InstantSearch's facetFilters convention): a single
    // one-entry OR-group filtering `attribute:on`.
    const widget = configure( { facetFilters: [ [ `${ attribute }:${ on }` ] ] } );
    search.addWidgets( [ widget ] );
    configureRef.current = widget;
  }

  // search.helper is null until the instance start()s — the default-on path below runs before
  // that, and the initial search picks up the just-added widget automatically.
  if ( search.helper ) search.helper.search();
}

function initToggleRefinementWidget() {
  const tgblAlgoliaDataJSON = window.atob( tgblSearchsyncDataEncoded );
  const tgblAlgoliaData = jQuery.parseJSON( tgblAlgoliaDataJSON );

  const elements = document.querySelectorAll( "[data-filter-type='toggleRefinement']" );

  elements.forEach( ( element ) => {
    const indexName = element.getAttribute( 'data-index' );
    if ( ! indexName ) return;

    const search = getInstance( indexName );
    if ( ! search ) return;

    const widgetID = element.getAttribute( 'data-id' );
    const widgetData = tgblAlgoliaData[ widgetID ] || {};
    const attribute = widgetData.attribute;
    const on = widgetData.on;

    const checkbox = element.querySelector( 'input[type="checkbox"]' );
    if ( ! checkbox ) return;

    // Mirror the refinement list's selected-state class so the item styles identically.
    const item = element.querySelector( '.tgbl-refinement-item' );
    const syncSelected = () => {
      if ( item ) item.classList.toggle( 'ais-RefinementList-item--selected', checkbox.checked );
    };

    const configureRef = { current: null };

    checkbox.addEventListener( 'change', () => {
      syncSelected();
      applyToggleFilter( { search, configureRef, checked: checkbox.checked, attribute, on } );
    } );

    // default-on: if the template rendered the box already checked, apply before the instance
    // starts so the first search is already filtered.
    if ( checkbox.checked ) {
      syncSelected();
      applyToggleFilter( { search, configureRef, checked: true, attribute, on } );
    }

    registerWidget( element );
  } );
}

export { initToggleRefinementWidget };